/**
 * The paste offer (19/10/2026): one calm line above the containers card
 * while a campaign group copied on a board waits on the clipboard — any
 * board but the copied group's own. Paste here… opens the paste window,
 * ✕ empties the clipboard. A paste empties it too and opens the target group.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { CopyPlus, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { type GroupEntry, middleTruncate } from '@/features/drop-generator/components/campaign/campaign-scope';
import { type GroupCopyClip, pasteOffered } from '@/features/drop-generator/components/group-copy/clipboard/group-copy-clipboard';
import { useCountryName } from '@/features/drop-generator/components/group-copy/clipboard/use-country-name';
import { useGroupCopyClipboard } from '@/features/drop-generator/components/group-copy/clipboard/use-group-copy-clipboard';
import { PasteGroupDialog } from '@/features/drop-generator/components/group-copy/dialog/PasteGroupDialog';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

interface Props {
  countryKey: string;
  /** Every campaign group the board offers. */
  entries: GroupEntry[];
  current: GroupEntry | null;
  /** Opens a group on the board; absent where the board is locked to one. */
  onOpenGroup?: (groupKey: string) => void;
}

const facts = (clip: GroupCopyClip) =>
  [`${countryToFlagEmoji(clip.countryName)} ${clip.countryName}`, middleTruncate(clip.label, 40), `${clip.containers} container${clip.containers === 1 ? '' : 's'}`, `master ${clip.masterName}`].join(' · ');

const Line: FC<{ clip: GroupCopyClip; onPaste: () => void; onClear: () => void }> = ({ clip, onPaste, onClear }) => (
  <div role="region" aria-label="Copied campaign group" className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm">
    <CopyPlus className="h-4 w-4 shrink-0 text-primary" aria-hidden />
    <span className="line-clamp-2 min-w-0 flex-1 basis-52 break-words sm:line-clamp-none sm:truncate" title={clip.label}>
      <span className="text-muted-foreground">Copied: </span>
      {facts(clip)}
    </span>
    <Button type="button" size="sm" className="h-7" onClick={onPaste}>Paste here…</Button>
    <button type="button" aria-label="Forget the copied group" title="Forget the copied group" onClick={onClear} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const PasteBar: FC<Props> = ({ countryKey, entries, current, onOpenGroup }) => {
  const { clip, clear } = useGroupCopyClipboard();
  const countryName = useCountryName(countryKey);
  // The window keeps the clip it opened with: a paste empties the clipboard while it shrinks away.
  const [opened, setOpened] = useState<GroupCopyClip | null>(null);
  const [open, setOpen] = useState(false);
  const show = pasteOffered(clip, { countryKey, groupKey: current?.key ?? null });
  const dialogClip = opened ?? clip;
  const sameCountry = dialogClip?.source.countryKey === countryKey;
  const groups = sameCountry ? entries.filter((e) => e.key !== dialogClip?.source.groupKey) : entries;
  /** v2: groupKey null = pasted into other countries only — the board stays on its group. */
  const onPasted = (groupKey: string | null) => {
    clear();
    setOpen(false);
    if (groupKey) onOpenGroup?.(groupKey);
  };
  return (
    <>
      {show && clip ? <Line clip={clip} onPaste={() => { setOpened(clip); setOpen(true); }} onClear={clear} /> : null}
      {dialogClip ? (
        <PasteGroupDialog open={open} onClose={() => setOpen(false)} clip={dialogClip} countryKey={countryKey} countryName={countryName} groups={groups} openKey={current?.key ?? null} onPasted={onPasted} />
      ) : null}
    </>
  );
};
